import React, { useEffect, useRef, useState } from 'react';
import ButtonPrimary from '../Form/ButtonPrimary';
import { TbBrandWhatsapp, TbMessage, TbMessage2Question, TbMoodCheck, TbMoodHeart, TbMoodSearch, TbPhone } from 'react-icons/tb';
import { IMask, IMaskInput } from 'react-imask';
import { HiXMark } from 'react-icons/hi2';
import { useLoginContext } from '../../Contexts/LoginContext';
import { ImSpinner8 } from 'react-icons/im';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import CustomerService from '../../Services/CustomerService';
import CountdownTimer from './CountdownTimer';
import { hideKeyboard, isPhoneValid, sanitizeText } from '../../Utils/Helpers';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { useCartHook } from '../../Hooks/CartHook';
import ButtonClose from '../Form/ButtonClose';
import ButtonLoginSSO from './ButtonLoginSSO';
import { useTranslation } from 'react-i18next';
import { TokenManager } from '../../Services/TokenManager';
import InputPhone from '../Form/InputPhone';
import ButtonLogin from './ButtonLogin';

interface LoginStoreCounterProps {
  closeable?: boolean;
  onClose: CallableFunction;
}

const LoginStoreCounter = ({closeable, onClose}: LoginStoreCounterProps) => {

  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [userRegistered, setUserRegistered] = useState(false);
  const [userToken, setUserToken] = useState('');
  const [invalidToken, setInvalidToken] = useState(false);
  const [userHash, setUserHash] = useState('');
  const [errorPhone, setErrorPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [errorFirstName, setErrorFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errorLastName, setErrorLastName] = useState('');

  const { user, setUser, setShowLoginStoreCounter, generateToken, setGenerateToken, setExpiratedCode, isAuthenticatedBySSO } = useLoginContext();
  const { setMainLoading, setMainLoadingIcon, setMainLoadingIconError, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, isMobile  } = useLayoutContext();

  const [ loadingButton, setLoadingButton ] = useState(false);
  const [ loadingResendCode, setLoadingResendCode ] = useState(false);
  const [ isWhatsappAvailable, setIsWhatsappAvailable ] = useState<boolean | null>(null);
  const [ codeSent, setCodeSent ] = useState(false);
  const [ enableResendCode, setEnableResendCode ] = useState(false);
  const [ isWrongPhone, setIsWrongPhone ] = useState(false);
  const [ timeResendCode, setTimeResendCode ] = useState('');
  const [receiveCodeType, setReceiveCodeType] = useState<string>(() => {
    return localStorage.getItem('receive_code_type') || '';
  });

  const { orderType, getOrderTypeIdByName } = useOrderTypeContext();
  const { checkIsWhatsappAvailable, getCustomerCart } = useCartHook();

  const swiperRef = useRef<SwiperRef>(null);
  const [ step, setStep ] = useState(1);
  const [ icon, setIcon ] = useState<any>();
  const [ title, setTitle ] = useState('');
  const [ subtitle, setSubtitle ] = useState('');
  const [ message, setMessage ] = useState('');

  const formatPhoneNumber = (phoneNumber: string): string => {
    const phoneMask = IMask.createMask({
      mask: '(00) 00000-0000',
    });
    phoneMask.resolve(phoneNumber);
    return phoneMask.value;
  };

  const handleComplete = () => {
    setEnableResendCode(true);
  };

  useEffect(() => {
    if (receiveCodeType) {
      localStorage.setItem('receive_code_type', receiveCodeType);
    } else {
      localStorage.removeItem('receive_code_type');
    }
  }, [receiveCodeType]);

  useEffect(() => {
    if (codeSent && enableResendCode && !loadingResendCode) {
      setIsWrongPhone(true);
    }
  }, [codeSent, enableResendCode, loadingResendCode]);

  useEffect(() => {
    const fetchWhatsappAvailability = async () => {
      const available = await checkIsWhatsappAvailable();
      setIsWhatsappAvailable(available);
    };

    fetchWhatsappAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    //* Montando configurações para pular etapas
    //* quando o cliente clicar no "Prefiro continuar depois"
    if (user) {
      setPhone(user.mobile_phone);
      setUserRegistered(true);
      setFirstName(user.first_name);
      setLastName(user.last_name);
      setCodeSent(Boolean(generateToken));
      if (generateToken) {
        setTimeResendCode(generateToken.generated);
        setUserHash(generateToken.hash);
        setStep(3);
      }
    }
  }, [user, generateToken]);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideTo(step - 1);
    }
    switch (step) {
      case 2:
        setTitle(userRegistered ? t('login.store_counter.title_user_found') : t('login.store_counter.title_user_new'));
        setSubtitle(userRegistered ? t('login.store_counter.title_confirm_1') : `${t('login.store_counter.title_confirm_2')} <strong class='text-custom-primary'>${formatPhoneNumber(phone)}</strong>`);
        setMessage('');
        setIcon(userRegistered ? <TbMoodCheck size={50} className='zoomInIconNoDelay' /> : <TbMoodHeart size={50} className='zoomInIconNoDelay' />);
        break;
      case 3:
        setTitle(`${t('login.store_counter.title_confirm_3')}, ${firstName}!`);
        setSubtitle(`
          ${receiveCodeType === 'sms' 
            ? t('login.store_counter.subtitle_confirm_sms') 
            : t('login.store_counter.subtitle_confirm_whatsapp')
          } <strong class='text-custom-primary'>${formatPhoneNumber(phone)}</strong>`);
        setMessage(t('login.store_counter.title_confirm_message_1'));
        setIcon(<TbMessage2Question size={50} className='zoomInIconNoDelay' />);
        if ("OTPCredential" in window) {
          console.log("WebOTP API disponível");
          navigator.credentials
          .get({ otp: { transport: ["sms"] }} as any)
          .then(otp => { if (otp && (otp as any).code) setUserToken((otp as any).code); })
          .catch( err => {console.warn("WebOTP API falhou:", err);});
        }
        break;
      default:
        setTitle(t('login.store_counter.default_title'));
        setSubtitle(t('login.store_counter.default_subtitle'));
        setMessage(t('login.store_counter.title_confirm_message_2'));
        setIcon(<TbMoodSearch size={50} className='zoomInIcon' />);
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ phone, step, firstName, userRegistered, userToken ]);

  useEffect(() => {
    if (firstName && firstName.length && firstName.length < 2) {
      setErrorFirstName(t('form.validation.login_store_counter.invalid_first_name_min'));
    } else {
      setErrorFirstName('');
    }
    if (lastName && lastName.length && lastName.length < 2) {
      setErrorLastName(t('form.validation.login_store_counter.invalid_last_name_min'));
    } else {
      setErrorLastName('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName]);

  useEffect(() => {
    if (!phone) {
      // setReceiveCodeType('');
    } else if (isPhoneValid(phone)) {
      hideKeyboard();
      setErrorPhone('');
    } else {
      setErrorPhone(t('login.store_counter.invalid_phone'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  const isContinueValid = () => {
    return Boolean(firstName && firstName.length && firstName.length >= 2 && lastName && lastName.length && lastName.length >= 2);
  }

  const handleResendCode = () => {
    setUserToken('');
    setLoadingResendCode(true);
    handleSendCode(receiveCodeType, phone);
    setInvalidToken(false);
    setEnableResendCode(false);
  }

  const handleSearchPhone = async (receivingCodeMethod: string) => {
    setReceiveCodeType(receivingCodeMethod);
    try {
      setLoadingButton(true);
      const customerService = new CustomerService(orderType);
      const payload = {
        phone_number: phone,
        order_type: Number(getOrderTypeIdByName(orderType))
      }
      const profile = await customerService.searchByPhone(payload);

      if (profile) {
        setUser(profile);
        if (profile.token) {
          handleAuth(profile.token);
        } else {
          setStep(3);
          setUserRegistered(true);
          setFirstName(profile.first_name);
          setLastName(profile.last_name);
          setLoadingButton(true);
          handleSendCode(receivingCodeMethod, profile.mobile_phone);
        }
      }

    } catch (error) {
        setStep(2);
    } finally {
        setLoadingButton(false);
    }
  }

  const handleAuth = (token: string) => {
    TokenManager.setToken(token);
    setGenerateToken(null);
    getCustomerCart();
    setReceiveCodeType('');
    onClose();
  }

  const handleValidateToken = async () => {
    const payload = {
      user_token: userToken,
      user_hash: userHash,
      phone_number: phone
    }
    try {
      setLoadingButton(true);
      const customerService = new CustomerService(orderType);
      const validToken = await customerService.validateToken(payload);

      if (validToken.validated) {
        handleAuth(validToken.token);
      } else {
        setInvalidToken(true);
      }

    } catch (error) {
        console.error('Error validateToken:', error);
        setInvalidToken(true);
    } finally {
        setLoadingButton(false);
    }
  }

  const handleRegiterCustomer = async () => {

    const payload = {
      first_name: firstName,
      last_name: lastName,
      mobile_phone: phone
    }

    try {
      setLoadingButton(true);
      const customerService = new CustomerService(orderType);
      const register = await customerService.register(payload);

      if (register) {
        setUserRegistered(true);
        setFirstName(register.customer.first_name);
        setLastName(register.customer.last_name);
        setUser(register.customer);
        setStep(3);

        setLoadingButton(true);
        handleSendCode(receiveCodeType, register.customer.mobile_phone);
      }

    } catch (error) {
      setMainLoading(true);
      setMainLoadingIcon(false);
      setMainLoadingIconError(true);
      setMainLoadingCloseButton(true);
      setMainLoadingTitle(t('login.store_counter.error_regiter_title'));
      setMainLoadingMessage(t('login.store_counter.error_regiter_message'));
    } finally {
      setLoadingButton(false);
    }
  }

  const handleUpdateCustomer = async () => {
    // console.log('updateCustomer')
  }

  const handleSendCode = async (receiveCodeType: string, phone: string) => {
    const payload = {
      media: receiveCodeType,
      number: phone
  };

    try {
      const customerService = new CustomerService(orderType);
      const token = await customerService.generateToken(payload);

      if (token) {
        setCodeSent(!token.error);
        setUserHash(token.hash);
        setGenerateToken(token);
        setEnableResendCode(false);
        setTimeResendCode(token.generated);
        setExpiratedCode(false);
      } else {
        setInvalidToken(true);
      }

    } catch (error) {
      console.error('Error generateToken:', error);
    } finally {
      setLoadingResendCode(false);
      setLoadingButton(false);
    }
  }

  const handleLater = () => {
    setShowLoginStoreCounter(false);
    onClose();
  }

  const handleChangeNumber = () => {
    setUser(null);
    setGenerateToken(null);
    setUserToken('');
    setFirstName('');
    setLastName('');
    setUserRegistered(false);
    setIsWrongPhone(false);
    setInvalidToken(false);
    setReceiveCodeType('');
    setStep(1);
  }

  useEffect(() => {
    if (userToken.length === 6) {
      handleValidateToken();
      setInvalidToken(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken])


  const [ messageCodeSent, setMessageCodeSent ] = useState<string | null>(null);
  
  useEffect(() => {
      setMessageCodeSent(null);
      if (codeSent && !enableResendCode && receiveCodeType === 'sms' && isMobile) {
        setMessageCodeSent(`<div class='font-medium text-custom-primary leading-5 mt-1.5 mb-0.5 px-1 zoomInIcon'>${t('login.store_counter.code_message_sent_sms')}</div>`);
      } else if (codeSent && enableResendCode && !loadingResendCode) {
        setMessageCodeSent(`<div class='font-medium text-custom-primary leading-5 mt-1.5 mb-0.5 px-1 zoomInIcon'>${t('login.store_counter.code_message_sent_sms_resend')}</div>`);
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeSent, enableResendCode, receiveCodeType, isMobile, loadingResendCode])

  return (
    <div className="pt-4 flex gap-3 flex-col">

        {closeable && <ButtonClose fixedOnTop onClick={onClose} />}

        <div className='py-1 grid gap-2'>
          <h2 className='flex flex-col gap-3 items-center text-shopfood-title px-4'>
            { icon }
            <span className='text-xl font-extrabold text-shopfood-title leading-5'>{ title }</span>
          </h2>
          <div className='flex gap-0.5 px-2 flex-col items-center justify-center leading-5'>
            <span className='font-medium' dangerouslySetInnerHTML={{ __html: sanitizeText(subtitle) }} />
            <span className='hidden text-gray-500 text-sm'>{message}</span>
          </div>
        </div>

        <div className='flex flex-col p-2 md:p-4 bg-gray-50'>

          <Swiper
            ref={swiperRef}
            spaceBetween={0}
            slidesPerView={1}
            allowTouchMove={false}
            centeredSlides={true}
            keyboard={{enabled: false}}
            className='grid w-full'
            pagination={false}
            navigation={false}
            simulateTouch={false}
            autoHeight={false}
            >

            <SwiperSlide className='p-2 h-full'>
              {/* PHONE */}
              <div className="grid gap-2">
                <label htmlFor="phone" className="block font-semibold text-left text-shopfood-title self-start">
                  {t('login.store_counter.phone.label')}
                </label>
                <div className="relative flex items-center rounded-md shadow-sm">
                  <TbPhone size={20} className='absolute z-10 left-2' />
                  <InputPhone
                      id="phone"
                      value={phone}
                      onAccept={(value: string) => setPhone(value)}
                      disabled={loadingButton}
                      className="relative block w-full"
                  />
                    {(isPhoneValid(phone) && !loadingButton) &&
                      <button onClick={() => setPhone('')} className='absolute flex items-center justify-center right-2 text-gray-400 rounded-full p-1 hover:bg-gray-100 focus-visible:outline transition-width ease-in-out duration-200'>
                        <HiXMark aria-hidden="true" size={20} />
                      </button>
                    }
                   {errorPhone && <div className='absolute right-2 text-xs text-left text-shopfood-error font-medium'>{errorPhone}</div>}
                </div>
                {step === 1 &&
                  <div className={`grid gap-2 transition-all ease-in-out duration-250 pt-3 ${isPhoneValid(phone) ? 'opacity-100' : 'opacity-75'}`}>


                    <div className={`transition-all ease-in-out ${isPhoneValid(phone) ? 'font-bold text-shopfood-title' : 'text-gray-500'}`}>{t('login.store_counter.receive_code_title')}</div>

                    {(!phone || !receiveCodeType || (isPhoneValid(phone) && receiveCodeType === 'sms')) &&
                      <ButtonPrimary disabled={!isPhoneValid(phone) || loadingButton} onClick={() => handleSearchPhone('sms')} className='w-full h-10 my-1 gap-1.5'>
                        {loadingButton && receiveCodeType === 'sms' ? <><span><ImSpinner8 className="animate-spin" /></span><span>{t('login.store_counter.sending_code_sms_loading')}</span></> : <><span>{t('login.store_counter.sending_code_sms')}</span><TbMessage size={20} /></> }
                      </ButtonPrimary>
                    }

                    { (isWhatsappAvailable && (!phone || !receiveCodeType || (isPhoneValid(phone) && receiveCodeType === 'whatsapp'))) &&
                      <ButtonPrimary outline disabled={!isPhoneValid(phone) || loadingButton} onClick={() => handleSearchPhone('whatsapp')} className='w-full flex-col mb-1'>
                        <div className='flex items-center gap-1.5'>
                          {loadingButton && receiveCodeType === 'whatsapp' ? <><span><ImSpinner8 className="animate-spin" /></span><span>{t('login.store_counter.sending_code_whatsapp_loading')}</span></> : <><span>{t('login.store_counter.sending_code_whatsapp')}</span><TbBrandWhatsapp size={20} /></> }
                        </div>
                      </ButtonPrimary>
                    }

                  </div>
                }

                <ButtonLogin
                    forceSsoLogin={isAuthenticatedBySSO}
                    withLoading={isAuthenticatedBySSO}
                    labelLink={t('login.label_button_sso')} 
                />
              </div>
            </SwiperSlide>

            <SwiperSlide className='p-2 h-full'>
              {/* NAME */}
              {(step === 2) &&
                <div className='grid gap-1'>
                  <div className='flex flex-col w-full gap-2'>
                    <div className="flex flex-col gap-1 w-full">
                      <label htmlFor="first_name" className="block font-semibold text-left text-shopfood-title self-start">
                        {t('login.store_counter.first_name.label')}
                      </label>
                      <div className="relative flex items-center rounded-md  shadow-sm">
                        <IMaskInput
                          id="first_name"
                          mask={/^[a-zA-ZÀ-ÿ\s]*$/}
                          radix=""
                          value={firstName}
                          onAccept={(value) => setFirstName(value)}
                          unmask={true} // true|false|'typed'
                          placeholder=''
                          className='relative block w-full rounded py-2 px-3 ring-1 ring-gray-400 placeholder:text-gray-500 placeholder:font-normal placeholder:text-xs font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-200'
                        />
                      </div>
                      {errorFirstName &&
                        <div className='text-xs text-left text-shopfood-error'>{errorFirstName}</div>
                      }
                    </div>

                    <div className="flex flex-col gap-1 w-full">
                      <label htmlFor="last_name" className="block font-semibold text-left text-shopfood-title self-start">
                        {t('login.store_counter.last_name.label')}
                      </label>
                      <div className="relative flex items-center rounded-md shadow-sm">
                        <IMaskInput
                          id="last_name"
                          mask={/^[a-zA-ZÀ-ÿ\s]*$/}
                          radix=""
                          value={lastName}
                          onAccept={(value) => setLastName(value)}
                          unmask={true} // true|false|'typed'
                          placeholder=''
                          className='relative block w-full rounded py-2 px-3 ring-1 ring-gray-400 placeholder:text-gray-500 placeholder:font-normal placeholder:text-xs font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-200'
                        />
                      </div>
                      {errorLastName &&
                        <div className='text-xs text-left text-shopfood-error'>{errorLastName}</div>
                      }
                    </div>
                  </div>
                  <div className='flex'>
                    {!userRegistered
                      ? <ButtonPrimary onClick={handleRegiterCustomer} disabled={!isContinueValid() || loadingButton} className='justify-center items-center w-full mt-2 h-10'>
                          {!loadingButton ? t('login.store_counter.button_continue_label') : <span className='flex items-center justify-center'><ImSpinner8 className="animate-spin" /></span> }
                        </ButtonPrimary>
                      : <ButtonPrimary onClick={handleUpdateCustomer} disabled={!isContinueValid() || loadingButton} className='justify-center items-center w-full mt-2 h-10'>
                          {!loadingButton ? t('login.store_counter.button_update_label') : <span className='flex items-center justify-center'><ImSpinner8 className="animate-spin" /></span> }
                        </ButtonPrimary>
                    }
                  </div>

                  <ButtonLoginSSO className='mt-2' />
                </div>
              }
            </SwiperSlide>

            <SwiperSlide className='p-2 h-full'>
              {/* CODE */}
              {step === 3 &&
                <div className="grid gap-2">
                  <label htmlFor="code" className="block font-semibold text-left text-shopfood-title">
                    {t('login.store_counter.type_code')}
                  </label>
                  <div className="relative flex items-center rounded-md shadow-sm">
                    <IMaskInput
                      mask="000000"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      name="one-time-code"
                      id="code"
                      radix="."
                      value={userToken}
                      onAccept={(value) => setUserToken(value)}
                      unmask={true} // true|false|'typed'
                      placeholder='------'
                      className='relative tracking-[.25em] text-xl block w-full rounded border-0 py-2 pl-3 pr-12 ring-1 ring-gray-400 placeholder:text-gray-300 placeholder:text-xl placeholder:font-normal font-extrabold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-200'
                    />

                    {loadingButton && <span className='absolute text-xs right-20 mr-2 w-5 justify-center items-center'><ImSpinner8 className="animate-spin" /></span> }

                    {codeSent &&
                      <ButtonPrimary outline={!enableResendCode} onClick={handleResendCode} disabled={!enableResendCode || loadingResendCode} className='absolute items-center justify-center right-0 mr-2 h-8 w-20 text-xs text-nowrap text-xs'>
                        { !enableResendCode
                          ? <CountdownTimer  startDate={timeResendCode} durationInMinutes={3} onComplete={handleComplete} />
                          : enableResendCode && !loadingResendCode ? t('login.store_counter.resend_code_label') : <span className='flex items-center justify-center'><ImSpinner8 className="animate-spin" /></span>
                        }
                      </ButtonPrimary>
                    }

                  </div>

                  {invalidToken &&
                    <div className='text-xs text-left text-shopfood-error'>{t('login.store_counter.invalid_token_message')}</div>
                  }

                  {/* Mensagem informativa para o cliente */}
                  {messageCodeSent && 
                    <div className='min-h-20'>
                      <div dangerouslySetInnerHTML={{ __html: sanitizeText(messageCodeSent) }} />
                    </div>
                  }

                </div>
              }
            </SwiperSlide>
            
          </Swiper>

          <div className='mx-2 mt-3 mb-4 flex gap-2 justify-between text-sm text-gray-400'>
            <button onClick={handleLater} className='w-full p-2 leading-3 hover:text-custom-primary cursor-pointer transition-all ease-in-out duration-200'>
              {t('login.store_counter.button_continue_later_label')}
            </button>

            {(user || step === 2) &&
              <button onClick={handleChangeNumber} className={`w-full p-1 leading-3 hover:text-custom-primary cursor-pointer transition-all ease-in-out duration-200
                ${isWrongPhone && 'text-shopfood-error font-bold animate-pulse'}
              `}>
                {t('login.store_counter.button_wrong_number_label')}{isWrongPhone && ('?')}
              </button>
            }
          </div>

        </div>
    </div>
  )
}

export default LoginStoreCounter;